import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { usePrayerJournal } from './hooks/usePrayerJournal';
import { useGeminiLive } from './hooks/useGeminiLive';
import { PrayerRequest, PrayerStatus, PrayerType, FunctionCallHandler, PrayerList } from './types';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import PrayerCard from './components/PrayerCard';
import PrayerFormModal from './components/PrayerFormModal';
import SettingsModal from './components/SettingsModal';
import ConfirmationModal from './components/ConfirmationModal';
import VoiceControlUI from './components/VoiceControlUI';
import PrayerTimeView from './components/PrayerTimeView';
import { fetchBibleVerseRecommendation, fetchVerseAudio } from './services/geminiService';
import { FunctionCall } from '@google/genai';
import { useDarkMode } from './hooks/useDarkMode';
import { useGoogleDriveSync } from './hooks/useGoogleDriveSync';
import useDebounce from './hooks/useDebounce';

const App: React.FC = () => {
    const { 
        prayers, lists, addPrayer, updatePrayer, deletePrayer, 
        findPrayerByTitle, findListByName, addList, updateList, deleteList,
        setJournalState
    } = usePrayerJournal();
    
    const [activeListId, setActiveListId] = useState('all');
    const [editingPrayer, setEditingPrayer] = useState<PrayerRequest | null>(null);
    const [prayerToDelete, setPrayerToDelete] = useState<PrayerRequest | null>(null);
    const [listToDelete, setListToDelete] = useState<PrayerList | null>(null);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [hideAnswered, setHideAnswered] = useState(false);
    const [isMicBarVisible, setIsMicBarVisible] = useState(true);
    const [theme, setTheme] = useDarkMode();
    const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
    
    const [prayerTimeState, setPrayerTimeState] = useState<{ active: boolean; prayers: PrayerRequest[]; currentIndex: number }>({
        active: false, prayers: [], currentIndex: 0,
    });
    const [favoriteBibleVersion, setFavoriteBibleVersion] = useState('NIV');
    const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
    const [isAutoSyncEnabled, setIsAutoSyncEnabled] = useState<boolean>(() => {
        const storedValue = localStorage.getItem('autoSyncEnabled');
        return storedValue ? JSON.parse(storedValue) : true; // Default to enabled
    });


    const { 
        isListening, isSpeaking, isProcessing, userTranscript, aiTranscript, 
        toggleListening, permissionState, functionCallQueue, setFunctionCallQueue, 
        sendToolResponse, addBase64AudioToQueue 
    } = useGeminiLive();

    const driveSync = useGoogleDriveSync();

    // Debounce journal state for auto-syncing
    const journalState = useMemo(() => ({ prayers, lists }), [prayers, lists]);
    const debouncedJournalState = useDebounce(journalState, 2000);

    // Persist auto-sync setting
    useEffect(() => {
        localStorage.setItem('autoSyncEnabled', JSON.stringify(isAutoSyncEnabled));
    }, [isAutoSyncEnabled]);

    useEffect(() => {
        // Auto-sync when debounced state changes, user is signed in, and auto-sync is enabled
        if (driveSync.isSignedIn && debouncedJournalState && isAutoSyncEnabled) {
            driveSync.syncData(debouncedJournalState);
        }
    }, [debouncedJournalState, driveSync.isSignedIn, driveSync.syncData, isAutoSyncEnabled]);

    useEffect(() => {
        const handleInstallPrompt = (e: BeforeInstallPromptEvent) => {
            e.preventDefault();
            // Do not show install prompt if app is already installed
            if (window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone) {
                return;
            }
            setInstallPrompt(e);
        };
        window.addEventListener('beforeinstallprompt', handleInstallPrompt);
        return () => window.removeEventListener('beforeinstallprompt', handleInstallPrompt);
    }, []);

    const handleInstallClick = async () => {
        if (!installPrompt) return;
        installPrompt.prompt();
        const { outcome } = await installPrompt.userChoice;
        if (outcome === 'accepted') {
            console.log('User accepted the install prompt');
        } else {
            console.log('User dismissed the install prompt');
        }
        setInstallPrompt(null);
    };

    const functionCallHandler = useMemo<FunctionCallHandler>(() => ({
        addPrayerRequest: async (args) => {
            const prayerType = Object.values(PrayerType).find(t => t.toLowerCase() === args.type.toLowerCase()) || PrayerType.PETITION;
            let list = lists.find(l => l.name.toLowerCase() === (args.listName || '').toLowerCase());
            if (!list) list = lists.find(l => l.id === '1'); // Default list
            
            const newPrayerData = {
                title: args.title,
                requestor: args.requestor || 'Me',
                type: prayerType,
                details: args.details,
                listId: list!.id
            };

            const newPrayer = addPrayer(newPrayerData);
            fetchBibleVerseRecommendation(newPrayerData).then(rec => updatePrayer(newPrayer.id, { bibleVerseRecommendation: rec }));
            return `I've added a prayer for ${args.requestor} to the ${list!.name} list.`;
        },
        updatePrayerRequest: async (args) => {
            const prayer = findPrayerByTitle(args.title);
            if (!prayer) return `I couldn't find a prayer titled "${args.title}".`;
            updatePrayer(prayer.id, { status: PrayerStatus.ANSWERED, answeredDetails: args.answeredDetails });
            return `Okay, I've marked "${args.title}" as answered. That's wonderful news.`;
        },
        findPrayerRequests: async (args) => {
            if (args.listName) {
                const list = lists.find(l => l.name.toLowerCase() === args.listName.toLowerCase());
                if (list) {
                    setActiveListId(list.id);
                    return `Okay, showing the ${list.name} list.`;
                }
                return `I couldn't find a list named ${args.listName}.`;
            }
            setActiveListId('all');
            return "Showing all prayers.";
        },
        startPrayerTime: async (args) => {
            let prayersToStart = prayers.filter(p => p.status !== PrayerStatus.ANSWERED);
             if (args.listName) {
                const list = lists.find(l => l.name.toLowerCase() === args.listName.toLowerCase());
                if (list) {
                    prayersToStart = prayersToStart.filter(p => p.listId === list.id);
                }
            }
            if (prayersToStart.length === 0) return "There are no pending prayers to review.";
            setPrayerTimeState({ active: true, prayers: prayersToStart, currentIndex: 0 });
            const firstPrayer = prayersToStart[0];
            return `Let's begin prayer time. The first prayer is for ${firstPrayer.requestor}, titled: ${firstPrayer.title}. The details are: ${firstPrayer.details}`;
        },
        recommendAndReadVerse: async (args) => {
            const prayer = findPrayerByTitle(args.title);
            if (!prayer) return `I couldn't find a prayer titled "${args.title}".`;
            
            const recommendation = prayer.bibleVerseRecommendation || await fetchBibleVerseRecommendation(prayer);
            if(!prayer.bibleVerseRecommendation && recommendation) {
                updatePrayer(prayer.id, { bibleVerseRecommendation: recommendation });
            }
            if (!recommendation) return `I couldn't find a verse for "${args.title}".`;

            const verseRef = recommendation.split(' - ')[0];
            const audioData = await fetchVerseAudio(verseRef, favoriteBibleVersion);
            if (audioData) {
                addBase64AudioToQueue(audioData);
                return `Of course. Reading ${verseRef}.`;
            }
            return `I'm sorry, I couldn't get the audio for that verse.`;
        },
        setFavoriteBibleVersion: async (args) => {
            setFavoriteBibleVersion(args.version);
            return `Okay, I've set your favorite Bible version to ${args.version}.`;
        },
        createPrayerList: async (args) => {
            const existing = lists.find(l => l.name.toLowerCase() === args.name.toLowerCase());
            if (existing) return `You already have a list named ${args.name}.`;
            addList(args.name);
            return `I've created the ${args.name} list for you.`;
        },
        movePrayerToList: async (args) => {
            const prayer = findPrayerByTitle(args.prayerTitle);
            const list = lists.find(l => l.name.toLowerCase() === args.listName.toLowerCase());
            if (!prayer) return `I couldn't find the prayer "${args.prayerTitle}".`;
            if (!list) return `I couldn't find the list "${args.listName}".`;
            updatePrayer(prayer.id, { listId: list.id });
            return `Okay, I've moved "${args.prayerTitle}" to the ${args.listName} list.`;
        },
        renamePrayerRequest: async (args) => {
            const prayer = findPrayerByTitle(args.oldTitle);
            if (!prayer) {
                return `I couldn't find a prayer titled "${args.oldTitle}".`;
            }
            updatePrayer(prayer.id, { title: args.newTitle });
            return `Okay, I've renamed the prayer to "${args.newTitle}".`;
        },
        renamePrayerList: async (args) => {
            const list = findListByName(args.oldName);
            if (!list) {
                return `I couldn't find a list named "${args.oldName}".`;
            }
            if (list.id === '1') { // "General" list
                return "I'm sorry, the General list cannot be renamed.";
            }
            const existingWithNewName = findListByName(args.newName);
            if (existingWithNewName) {
                return `A list named "${args.newName}" already exists. Please choose a different name.`;
            }
            updateList(list.id, args.newName);
            return `I've renamed the "${args.oldName}" list to "${args.newName}".`;
        },
        deletePrayerRequest: async (args) => {
            const prayer = findPrayerByTitle(args.title);
            if (!prayer) {
                return `I couldn't find a prayer titled "${args.title}".`;
            }
            deletePrayer(prayer.id);
            return `I have deleted the prayer: "${args.title}".`;
        },
        deletePrayerList: async (args) => {
            const list = findListByName(args.name);
            if (!list) {
                return `I couldn't find a list named "${args.name}".`;
            }
            if (list.id === '1') { // "General" list
                return "I'm sorry, the General list cannot be deleted.";
            }
            deleteList(list.id);
            return `Okay, I've deleted the "${args.name}" list. Any prayers it contained have been moved to your General list.`;
        }
    }), [addPrayer, updatePrayer, findPrayerByTitle, prayers, lists, favoriteBibleVersion, addBase64AudioToQueue, addList, findListByName, updateList, deletePrayer, deleteList]);
    
    useEffect(() => {
        if (functionCallQueue.length > 0) {
            const [fc] = functionCallQueue;
            setFunctionCallQueue(q => q.slice(1));
            const handle = async (funcCall: FunctionCall) => {
                const { name, args, id } = funcCall;
                let result = `Unknown function: ${name}`;
                if (name in functionCallHandler) {
                    try { // @ts-ignore
                        result = await functionCallHandler[name](args);
                    } catch (error) { result = `There was an error trying to perform that action.`; }
                }
                sendToolResponse({ functionResponses: { id, name, response: { result } } });
            };
            handle(fc);
        }
    }, [functionCallQueue, functionCallHandler, sendToolResponse, setFunctionCallQueue]);

    const handleOpenForm = (prayer: PrayerRequest | null) => {
        setEditingPrayer(prayer);
        setIsFormOpen(true);
    };

    const handleFormSubmit = (prayerData: Omit<PrayerRequest, 'id' | 'date'>) => {
        if (editingPrayer) {
            updatePrayer(editingPrayer.id, prayerData);
        } else {
            const { status, answeredDetails, updatedAt, ...newPrayerData } = prayerData;
            const newPrayer = addPrayer(newPrayerData);
            fetchBibleVerseRecommendation(newPrayerData).then(rec => updatePrayer(newPrayer.id, { bibleVerseRecommendation: rec }));
        }
        setIsFormOpen(false);
        setEditingPrayer(null);
    };

    const handleConfirmDeletePrayer = () => {
        if (prayerToDelete) {
            deletePrayer(prayerToDelete.id);
            setPrayerToDelete(null);
        }
    };
    
    const handleConfirmDeleteList = () => {
        if (listToDelete) {
            if (activeListId === listToDelete.id) {
                setActiveListId('all');
            }
            deleteList(listToDelete.id);
            setListToDelete(null);
        }
    };

    const handleConfirmRestore = async () => {
        setShowRestoreConfirm(false);
        const restoredData = await driveSync.restoreData();
        if (restoredData) {
            setJournalState(restoredData);
            alert("Journal successfully restored from Google Drive.");
        }
    };

    const handleRecommendVerse = async (prayer: PrayerRequest) => {
        const recommendation = await fetchBibleVerseRecommendation(prayer);
        updatePrayer(prayer.id, { bibleVerseRecommendation: recommendation });
    };

    const handleNextPrayer = useCallback(() => {
        if (!prayerTimeState.active) return;
        const nextIndex = prayerTimeState.currentIndex + 1;
        if (nextIndex < prayerTimeState.prayers.length) {
            setPrayerTimeState(s => ({ ...s, currentIndex: nextIndex }));
        } else {
            setPrayerTimeState({ active: false, prayers: [], currentIndex: 0 });
        }
    }, [prayerTimeState]);

    const filteredPrayers = useMemo(() => {
        let prayersToFilter = prayers;
        if (activeListId !== 'all') {
            prayersToFilter = prayers.filter(p => p.listId === activeListId);
        }
        if (hideAnswered) {
            prayersToFilter = prayersToFilter.filter(p => p.status !== PrayerStatus.ANSWERED);
        }
        return prayersToFilter;
    }, [prayers, activeListId, hideAnswered]);
    
    const activeListName = lists.find(l => l.id === activeListId)?.name || 'All Prayers';

    return (
        <div className="h-screen w-screen flex bg-gray-100 dark:bg-gray-800 font-sans overflow-hidden">
            <Sidebar 
                lists={lists}
                activeListId={activeListId}
                onSelectList={(id) => {
                    setActiveListId(id);
                    setIsSidebarOpen(false);
                }}
                onAddList={addList}
                onDeleteList={(listId) => {
                    const list = lists.find(l => l.id === listId);
                    if (list) setListToDelete(list);
                }}
                isOpen={isSidebarOpen}
                onClose={() => setIsSidebarOpen(false)}
            />
            {isSidebarOpen && (
                <div 
                    className="fixed inset-0 bg-black bg-opacity-50 z-20 md:hidden"
                    onClick={() => setIsSidebarOpen(false)}
                ></div>
            )}
            <div className="flex-1 flex flex-col h-screen">
                <Header 
                    title={activeListName}
                    onAddPrayer={() => handleOpenForm(null)}
                    onStartPrayerTime={() => functionCallHandler.startPrayerTime({listName: activeListName === 'All Prayers' ? undefined : activeListName})}
                    onShowSettings={() => setIsSettingsOpen(true)}
                    onToggleSidebar={() => setIsSidebarOpen(true)}
                    showInstallPrompt={!!installPrompt}
                    onInstall={handleInstallClick}
                />
                <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 transition-all" style={{paddingBottom: isMicBarVisible ? '120px' : '2.5rem'}}>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                        {filteredPrayers.length > 0 ? (
                            filteredPrayers.map(prayer => 
                                <PrayerCard 
                                    key={prayer.id} 
                                    prayer={prayer}
                                    onEdit={handleOpenForm}
                                    onDelete={setPrayerToDelete}
                                    onRecommendVerse={handleRecommendVerse}
                                />)
                        ) : (
                            <div className="col-span-full text-center py-16 bg-white dark:bg-gray-800 rounded-lg shadow">
                                <h3 className="text-xl text-gray-700 dark:text-gray-300">This list is empty.</h3>
                                <p className="text-gray-500 dark:text-gray-400 mt-2">Click "Add Prayer" to get started.</p>
                            </div>
                        )}
                    </div>
                </main>
            </div>
            
            {isFormOpen && (
                <PrayerFormModal 
                    isOpen={isFormOpen}
                    onClose={() => setIsFormOpen(false)}
                    onSubmit={handleFormSubmit}
                    prayerToEdit={editingPrayer}
                    lists={lists}
                />
            )}
            
            {isSettingsOpen && (
                <SettingsModal
                    isOpen={isSettingsOpen}
                    onClose={() => setIsSettingsOpen(false)}
                    currentVersion={favoriteBibleVersion}
                    onSaveVersion={setFavoriteBibleVersion}
                    hideAnswered={hideAnswered}
                    onToggleHideAnswered={setHideAnswered}
                    currentTheme={theme}
                    onSetTheme={setTheme}
                    // Google Drive Sync props
                    isSignedIn={driveSync.isSignedIn}
                    isSyncing={driveSync.isSyncing}
                    lastSyncDate={driveSync.lastSyncDate}
                    userProfile={driveSync.userProfile}
                    onSignIn={driveSync.signIn}
                    onSignOut={driveSync.signOut}
                    onSyncNow={() => driveSync.syncData(journalState)}
                    onRestore={() => setShowRestoreConfirm(true)}
                    isAutoSyncEnabled={isAutoSyncEnabled}
                    onToggleAutoSync={setIsAutoSyncEnabled}
                />
            )}
            
            {prayerToDelete && (
                <ConfirmationModal
                    isOpen={!!prayerToDelete}
                    onClose={() => setPrayerToDelete(null)}
                    onConfirm={handleConfirmDeletePrayer}
                    title="Delete Prayer Request"
                    message={`Are you sure you want to delete the prayer "${prayerToDelete.title}"? This action cannot be undone.`}
                />
            )}
            
            {listToDelete && (
                 <ConfirmationModal
                    isOpen={!!listToDelete}
                    onClose={() => setListToDelete(null)}
                    onConfirm={handleConfirmDeleteList}
                    title="Delete Prayer List"
                    message={`Are you sure you want to delete the list "${listToDelete.name}"? All prayers in this list will be moved to "General". This action cannot be undone.`}
                />
            )}

            {showRestoreConfirm && (
                <ConfirmationModal
                    isOpen={showRestoreConfirm}
                    onClose={() => setShowRestoreConfirm(false)}
                    onConfirm={handleConfirmRestore}
                    title="Restore from Google Drive"
                    message="Are you sure you want to restore your journal from Google Drive? This will overwrite your current local data."
                />
            )}

            {prayerTimeState.active && (
                <PrayerTimeView 
                    currentPrayer={prayerTimeState.prayers[prayerTimeState.currentIndex]}
                    onNext={handleNextPrayer}
                    onClose={() => setPrayerTimeState({ active: false, prayers: [], currentIndex: 0 })}
                    total={prayerTimeState.prayers.length}
                    currentIndex={prayerTimeState.currentIndex}
                />
            )}
            
            <VoiceControlUI 
                isListening={isListening} isSpeaking={isSpeaking} isProcessing={isProcessing}
                userTranscript={userTranscript} aiTranscript={aiTranscript}
                onToggleListening={toggleListening} permissionState={permissionState}
                isVisible={isMicBarVisible}
                onToggleVisibility={() => setIsMicBarVisible(prev => !prev)}
            />
        </div>
    );
};

export default App;